import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { XCircle } from 'lucide-react';
import type { Trip } from '../../types/trip';
import { mensajeErrorViaje } from '../../utils/trips';

interface TripCancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  trip: Trip | null;
  onCancelar: (tripId: string, reason: string) => Promise<unknown>;
}

/**
 * FE-11 — cancelar NO borra el viaje: colapsa su ventana y conserva la fila
 * con quién lo programó y por qué (PRODUCT.md §4.3). Por eso la razón es
 * obligatoria y por eso el texto del modal no dice "eliminar".
 */
export function TripCancelModal({ isOpen, onClose, trip, onCancelar }: TripCancelModalProps) {
  const [reason, setReason] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) { setReason(''); setError(null); }
  }, [isOpen]);

  const enviar = async () => {
    if (!trip) return;
    if (!reason.trim()) { setError('La razón es obligatoria — queda en la novedad del viaje.'); return; }
    setEnviando(true);
    setError(null);
    try {
      await onCancelar(trip.id, reason.trim());
      onClose();
    } catch (err) {
      setError(mensajeErrorViaje(err, 'cancelar'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Cancelar viaje · ${trip?.vehiclePlate ?? ''}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={enviando}>Volver</Button>
          <Button variant="danger" onClick={enviar} isLoading={enviando} leftIcon={<XCircle size={16} />}>
            Cancelar viaje
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
          {trip ? <><strong>{trip.origin}</strong> → <strong>{trip.destination}</strong>. </> : null}
          El viaje no se borra: queda registrado como cancelado con su razón, y la franja
          horaria vuelve a estar libre para programar otro.
        </p>

        <Textarea
          label="Razón de la cancelación (obligatoria)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="El cliente aplazó la entrega para la próxima semana."
        />

        {error && (
          <p style={{ margin: 0, padding: '0.625rem 0.875rem', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius-md)', color: '#EF4444', fontSize: '12px', fontWeight: 600 }}>
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
